import * as XLSX from 'xlsx';

import { Feedback, FeedbackStats } from '../types';

/**
 * Utilitários de exportação de feedbacks para Excel.
 *
 * Usa a lib `xlsx` ja instalada no projeto. Sem dependencias novas.
 * Não expõe dados sensíveis: respeita `is_anonymous` (não escreve nome quando
 * o backend já entrega `user: null` para anônimos).
 */

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const buildFilename = (prefix: string): string => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const mi = String(now.getMinutes()).padStart(2, '0');
  return `${prefix}_${yyyy}${mm}${dd}_${hh}${mi}.xlsx`;
};

interface ExportOptions {
  feedbacks: Feedback[];
  stats?: FeedbackStats | null;
  filename?: string;
}

export function exportFeedbacksToExcel({ feedbacks, stats, filename }: ExportOptions): void {
  const workbook = XLSX.utils.book_new();

  const feedbackRows = feedbacks.map((item) => ({
    ID: item.id,
    Usuário: item.is_anonymous ? 'Anônimo' : item.user?.name ?? '',
    Email: item.is_anonymous ? '' : item.user?.email ?? '',
    Escopo:
      item.display_label
      ?? (item.workflow?.name ? `${item.workflow.name} (v${item.workflow.version})` : 'Plataforma (geral)'),
    'Versão do fluxo': item.workflow?.version ?? '',
    Nota: item.rating,
    Comentário: item.comment ?? '',
    Anônimo: item.is_anonymous ? 'Sim' : 'Não',
    'Criado em': formatDate(item.created_at),
    'Atualizado em': formatDate(item.updated_at),
  }));

  const feedbacksSheet = XLSX.utils.json_to_sheet(feedbackRows);
  feedbacksSheet['!cols'] = [
    { wch: 6 },   // ID
    { wch: 25 },  // Usuário
    { wch: 30 },  // Email
    { wch: 36 },  // Escopo
    { wch: 18 },  // Versão
    { wch: 6 },   // Nota
    { wch: 50 },  // Comentário
    { wch: 10 },  // Anônimo
    { wch: 18 },  // Criado em
    { wch: 18 },  // Atualizado em
  ];
  XLSX.utils.book_append_sheet(workbook, feedbacksSheet, 'Feedbacks');

  if (stats) {
    const summaryRows: Array<{ Métrica: string; Valor: string | number }> = [
      { Métrica: 'Total de feedbacks', Valor: stats.total },
      { Métrica: 'Média geral', Valor: stats.average_rating.toFixed(2) },
      { Métrica: '1 estrela', Valor: stats.distribution.rating_1 },
      { Métrica: '2 estrelas', Valor: stats.distribution.rating_2 },
      { Métrica: '3 estrelas', Valor: stats.distribution.rating_3 },
      { Métrica: '4 estrelas', Valor: stats.distribution.rating_4 },
      { Métrica: '5 estrelas', Valor: stats.distribution.rating_5 },
    ];
    const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
    summarySheet['!cols'] = [{ wch: 24 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumo');

    if (stats.top_workflows.length > 0) {
      const topRows = stats.top_workflows.map((wf) => ({
        Escopo: wf.workflow_name ?? 'Plataforma (geral)',
        'ID do fluxo': wf.workflow_id ?? '',
        'Total de avaliações': wf.total,
        'Média de notas': wf.average_rating.toFixed(2),
      }));
      const topSheet = XLSX.utils.json_to_sheet(topRows);
      topSheet['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 20 }, { wch: 16 }];
      XLSX.utils.book_append_sheet(workbook, topSheet, 'Top fluxos');
    }
  }

  XLSX.writeFile(workbook, filename ?? buildFilename('feedbacks'));
}
